/**
 * elkLayout.js — ELK-based layered DAG layout with fork/join-aware spine
 * alignment, per-branch column isolation, and orthogonal edge routing with
 * obstacle avoidance.
 *
 * This replaces the hand-rolled Sugiyama in dagLayout.js for production use.
 * ELK's `layered` algorithm with `BRANDES_KOEPF` node placement and `BALANCED`
 * fixed alignment delivers the Conductor OSS-style visual quality:
 *
 *   • Fork/join spine alignment — JOIN lands on the same X as its matching
 *     FORK automatically, because Brandes-Köpf balances the four alignment
 *     candidates (leftmost-upper, leftmost-lower, rightmost-upper,
 *     rightmost-lower) and averages them.
 *
 *   • Per-branch column isolation — each branch of a SWITCH / FORK_JOIN gets
 *     its own horizontal slot because ELK partitions nodes by layer and
 *     minimizes crossings within each layer independently.
 *
 *   • Orthogonal edge routing — edges are drawn as right-angle paths that go
 *     AROUND node rectangles, not through them. Bend points are computed by
 *     ELK and returned in `edge.sections[0].bendPoints`.
 *
 *   • Edge label placement — labels ("true", "false", "auto", "health",
 *     "branch_0" …) get inline positions on the edge they annotate, so
 *     they sit on the line instead of floating into neighbouring nodes.
 *
 * Async: ELK runs off the main thread via Web Worker. Callers must `await`.
 *
 * Usage:
 *   import { elkLayoutDAG } from '../utils/elkLayout';
 *   const { nodes, edges } = await elkLayoutDAG(nodes, connections, {
 *     nodeWidth: 350, nodeHeight: 100
 *   });
 */

import ELK from 'elkjs/lib/elk.bundled.js';

// Singleton ELK instance — spawns a Web Worker the first time it's used.
// Subsequent calls reuse the same worker.
let _elk = null;
function getElk() {
  if (!_elk) _elk = new ELK();
  return _elk;
}

/**
 * Layout a DAG using ELK's layered algorithm.
 *
 * @param {Array<{id: string, type?: string, config?: object, label?: string}>} nodes
 * @param {Array<{from: string, to: string, fromPort?: string, label?: string}>} connections
 * @param {Object} opts
 * @param {number} [opts.nodeWidth=350]   Node box width (matches Conductor OSS Canvas node).
 * @param {number} [opts.nodeHeight=100]  Node box height.
 * @param {number} [opts.rankSep=120]     Vertical gap between adjacent layers.
 * @param {number} [opts.nodeSep=60]      Horizontal gap between nodes in same layer.
 * @param {number} [opts.padding=80]      Padding around the entire layout.
 * @param {string} [opts.rankDir='TB']    Direction: 'TB' (top-bottom) or 'LR' (left-right).
 * @returns {Promise<{
 *   nodes: Array<{id, x, y, width, height, ...}>,
 *   edges: Array<{from, to, bendPoints: Array<{x, y}>, labelPosition?: {x, y}}>,
 * }>}
 */
export async function elkLayoutDAG(nodes, connections, opts = {}) {
  const {
    nodeWidth = 350,
    nodeHeight = 100,
    rankSep = 120,
    nodeSep = 60,
    padding = 80,
    rankDir = 'TB',
  } = opts;

  if (!nodes || nodes.length === 0) {
    return { nodes: [], edges: [] };
  }

  // Direction mapping: Canvas uses top-bottom by default; ELK accepts DOWN/RIGHT/UP/LEFT.
  const direction = rankDir === 'LR' ? 'RIGHT' : 'DOWN';

  // Build ELK graph input. Nodes declare width/height so ELK reserves space;
  // edges carry label hints so ELK reserves inline label slots that don't
  // overlap neighbouring nodes.
  const elkGraph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': direction,

      // Brandes-Köpf node placement — the algorithm that gives Conductor OSS its
      // central-spine look. `BALANCED` averages the four alignment candidates
      // so paired FORK/JOIN nodes end up on the same X coordinate.
      'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
      'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',

      // Crossing minimization — LAYER_SWEEP with high thoroughness.
      // 20 is the practical quality ceiling; above that returns diminishing
      // improvement on graphs under 100 nodes (our stress-test is 30).
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.thoroughness': '20',

      // Orthogonal edge routing — right-angle bends that route AROUND node
      // shapes, not through them. This is the single most important fix
      // for the "edges drawn over other shapes" artifact.
      'elk.edgeRouting': 'ORTHOGONAL',

      // Spacing — generous enough that labels fit between nodes without
      // overlapping, tight enough that the whole DAG fits on-screen at
      // zoomToFit for 30+ node workflows.
      'elk.spacing.nodeNode': String(nodeSep),
      'elk.layered.spacing.nodeNodeBetweenLayers': String(rankSep),
      'elk.layered.spacing.edgeNodeBetweenLayers': '30',
      'elk.layered.spacing.edgeEdgeBetweenLayers': '20',
      'elk.spacing.edgeLabel': '8',

      // Handle nested subworkflow groupings correctly if/when we introduce
      // composite nodes. Safe to enable unconditionally — no-op for flat graphs.
      'elk.hierarchyHandling': 'INCLUDE_CHILDREN',

      // Padding around the whole graph so the fitted bounding box has margin
      // when the canvas zoomToFit kicks in.
      'elk.padding': `[top=${padding},left=${padding},bottom=${padding},right=${padding}]`,
    },
    children: nodes.map((n) => ({
      id: n.id,
      width: nodeWidth,
      height: nodeHeight,
    })),
    edges: connections.map((c, i) => {
      const edge = {
        id: `e_${i}_${c.from}__${c.to}`,
        sources: [c.from],
        targets: [c.to],
      };
      // Declare a label whenever the connection carries one — either an explicit
      // `label` field, or a `fromPort` that will render as a branch pill.
      // The Canvas renderer shows a label pill for non-'default' / non-'out'
      // fromPorts (e.g. "true", "false", "auto", "health", "branch_0", "APPROVED"),
      // plus 'default' when the source is a condition node. We pass a superset
      // here so ELK reserves inline space and routes around the label rect —
      // the actual visibility is still decided at render time.
      const labelText = c.label || (c.fromPort && c.fromPort !== 'out' ? c.fromPort : null);
      if (labelText) {
        const text = String(labelText);
        edge.labels = [{
          text,
          width: Math.min(Math.max(20, text.length * 7 + 16), 140),
          height: 18,
          layoutOptions: {
            'elk.edgeLabels.placement': 'CENTER',
            'elk.edgeLabels.inline': 'true',
          },
        }];
      }
      return edge;
    }),
  };

  let result;
  try {
    result = await getElk().layout(elkGraph);
    // One-line diagnostic — confirms the worker ran and shows the bounding box
    // ELK produced. If width >> height drastically, branches didn't partition.
    // If width << height drastically, branches packed into vertical stack
    // (our current bug). If width/height ratio is near 1.0–2.0, layout is OK
    // and the problem is elsewhere (zoom fit, stale node positions, etc).
    // eslint-disable-next-line no-console
    console.log('[elkLayout] OK — bbox',
      Math.round(result.width || 0), '×', Math.round(result.height || 0),
      '| nodes', (result.children || []).length,
      '| edges', (result.edges || []).length,
      '| avg bends/edge',
      ((result.edges || []).reduce((a, e) => a + ((e.sections?.[0]?.bendPoints?.length) || 0), 0) / Math.max(1, (result.edges || []).length)).toFixed(1));
  } catch (err) {
    console.error('[elkLayout] ELK layout failed', err);
    throw err;
  }

  // Map ELK's output back to Canvas-shaped objects.
  const positionedNodes = (result.children || []).map((c) => {
    const original = nodes.find((n) => n.id === c.id) || {};
    return {
      ...original,
      x: c.x ?? 0,
      y: c.y ?? 0,
      width: c.width ?? nodeWidth,
      height: c.height ?? nodeHeight,
    };
  });

  const positionedEdges = (result.edges || []).map((e) => {
    const section = (e.sections && e.sections[0]) || null;
    const bendPoints = [];
    if (section) {
      if (section.startPoint) bendPoints.push({ x: section.startPoint.x, y: section.startPoint.y });
      if (section.bendPoints) for (const bp of section.bendPoints) bendPoints.push({ x: bp.x, y: bp.y });
      if (section.endPoint) bendPoints.push({ x: section.endPoint.x, y: section.endPoint.y });
    }
    const labelPosition = (e.labels && e.labels[0])
      ? { x: e.labels[0].x + (e.labels[0].width || 0) / 2, y: e.labels[0].y + (e.labels[0].height || 0) / 2 }
      : null;
    return {
      id: e.id,
      from: e.sources?.[0],
      to: e.targets?.[0],
      bendPoints,
      labelPosition,
    };
  });

  return {
    nodes: positionedNodes,
    edges: positionedEdges,
  };
}

/**
 * Drop-in synchronous fallback signature that returns a Promise. Useful if
 * you want to call ELK from a callsite that previously expected layoutDAG's
 * return shape of `Array<positioned nodes>`. Prefer `elkLayoutDAG` directly.
 */
export async function elkLayoutNodes(nodes, connections, opts = {}) {
  const { nodes: positioned } = await elkLayoutDAG(nodes, connections, opts);
  return positioned;
}
